/* eslint-disable prettier/prettier */
import ReactHtmlParser from 'react-html-parser';

import moment from 'moment';
import { isNumber } from 'util';

import { TFunction } from 'i18next';
import { Country } from 'models/country';
import airlinesJson from 'utils/airlines.json';
import airportsJson from 'utils/airports.json';

const defaultCountry = {
  id: 'BR',
  name: 'Brazil',
  culture: 'pt-BR',
  currency: 'BRL',
  documentType: 'CNPJ',
};

export const getCurrentCountry = (): Country =>
  JSON.parse(sessionStorage.getItem('@currentCountry') as string) || defaultCountry;

export const getCurrentLanguage = (): string => {
  const currentLanguage = sessionStorage.getItem('@currentLanguage');
  if (currentLanguage) return currentLanguage;

  const currentCountry = JSON.parse(sessionStorage.getItem('@currentCountry') as string);
  if (currentCountry) return currentCountry.culture;

  const availableTranslations = ['pt-BR', 'en-US', 'es-AR', 'es-CO', 'es-MX'];

  const userLanguages = window.navigator.languages;
  const mainLanguage = userLanguages[0];

  if (availableTranslations.includes(mainLanguage)) return mainLanguage;
  if (mainLanguage.startsWith('es')) return 'es-CO';
  if (mainLanguage.startsWith('pt')) return 'pt-BR';
  if (mainLanguage.startsWith('en')) return 'en-US';

  const specificLanguage = userLanguages.find((ul) => availableTranslations.find((at) => ul === at));
  if (specificLanguage) return specificLanguage;

  return '';
};

export const htmlT = (t: TFunction, str: string, options?: Record<string, unknown>) => {
  const unescapeHTML = (html: string): string => {
    const escapeEl = document.createElement('textarea');
    escapeEl.innerHTML = html;
    return escapeEl.textContent || str;
  };
  return ReactHtmlParser(unescapeHTML(t(str, options)));
};

export const getAirportFromInitials = (initial: string) =>
  airportsJson.find((airport) => airport.includes(initial))?.split(',')[0];

export const getAirlineForInitials = (initial: string) =>
  airlinesJson.find((airline) => airline.includes(initial))?.split(' (')[0];

export const getFlightClass = (cabin: string) => {
  switch (cabin) {
    case '0':
      return 'flightClass.executive';
    case '1':
      return 'flightClass.firstClass';
    case '2':
      return 'flightClass.economy';
    case '3':
      return 'flightClass.premiumEconomy';
    default:
      return '';
  }
};

export const extractErrorMessage = (error: any) => {
  let message = '';
  try {
    const errors = error.response.data.Errors || error.response.data.errors;
    errors.forEach((e: string) => (message += `${e}\n`));
    return message;
  } catch (_) {
    return undefined;
  }
};

export const extractErrorMessageFromArray = (errors: string[] | string[]) => {
  let message = '';
  try {
    errors.forEach((e: string | string) => (message += `${e}\n`));
  } catch (error) { }
  return message;
};

export const getAirlineLogo = (airlineCode: string) =>
  `https://images.rappi.com/web/travel/airlines/${airlineCode}.png`;

export const formatISODateToDDMMYYYY = (date?: string | Date): string => {
  if (!date) {
    return '';
  }

  return moment.utc(date).format('DD/MM/YYYY');
};

export const dateFormatWeekend = (date?: string | Date): string => {
  if (!date) {
    return '';
  }
  return moment(date).format('ddd[,] DD MMM');
};

export const formatDateToBack = (date: string) => {
  const dateParts = date.split('/');
  if (dateParts.length === 3) {
    return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
  }
  return date;
};

export const expirationHours = (expirationDate: Date | string): { hours: number; minutes: number } => {
  const now = moment();
  const endTime = moment(expirationDate);
  const hours = endTime.diff(now, 'hours');
  const minutes = endTime.diff(now, 'minutes');

  return { hours, minutes };
};

export const getFormData = (object: any) =>
  Object.keys(object).reduce((formData, key) => {
    if (isNumber(object[key])) {
      formData.append(key, object[key].toString().replace('.', ','));
    } else {
      formData.append(key, object[key]);
    }
    return formData;
  }, new FormData());

export const base64ToFile = async (url: string, extension: string, fileName?: string) => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new File([blob], fileName || '', { type: `image/${extension}` });
};

export const splitDate = (date: Date | string) => date.toString().split('T').first();

export const fittingString = (context: CanvasRenderingContext2D, str: string, maxWidth: number) => {
  let { width } = context.measureText(str);
  const ellipsis = '…';
  const ellipsisWidth = context.measureText(ellipsis).width;
  if (width <= maxWidth || width <= ellipsisWidth) {
    return str;
  }
  let len = str.length;
  while (width >= maxWidth - ellipsisWidth && len-- > 0) {
    str = str.substring(0, len);
    width = context.measureText(str).width;
  }
  return str + ellipsis;
};

export const formatNumberToCompact = (t: TFunction, value: number) => {
  if (value < 1e3) return value;
  if (value >= 1e3 && value < 1e6) return `${+(value / 1e3).toFixed(1)}${t('compactPostfixes.1e3')}`;
  if (value >= 1e6 && value < 1e9) return `${+(value / 1e6).toFixed(1)}${t('compactPostfixes.1e6')}`;
  if (value >= 1e9 && value < 1e12) return `${+(value / 1e9).toFixed(1)}${t('compactPostfixes.1e9')}`;
  if (value >= 1e12) return `${+(value / 1e12).toFixed(1)}${t('compactPostfixes.1e12')}`;
};

export const formatTrim = (text?: string) => {
  if (text !== undefined && text !== null && text !== '') {
    return text.trim();
  } else {
    return text;
  }
};

export const daysCount = (dateFrom?: Date, dateTo?: Date) => {
  if (dateTo !== undefined && dateFrom !== undefined) {
    const today = moment().startOf('day');
    let from = moment(dateFrom).startOf('day');
    const to = moment(dateTo).startOf('day');

    if (
      from.format('DD') === today.format('DD') &&
      from.format('MM') === today.format('MM') &&
      from.format('YYYY') === today.format('YYYY')
    ) {
      from = moment().startOf('day');
    }

    if (
      from.format('DD') === to.format('DD') &&
      from.format('MM') === to.format('MM') &&
      from.format('YYYY') === to.format('YYYY')
    ) {
      return 0;
    }

    return Math.ceil(to.diff(from, 'days', true));
  }
  return 0;
};

export const padLeadingZeros = (num, size) => {
  let s = num + '';
  while (s.length < size) s = '0' + s;
  return s;
};
